import * as React from 'react';
import { useSelector } from 'react-redux';

import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { ApplicationState } from '@app/store';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import { activePermission, Permission } from '@app/objects/Permission';
import { PetConfirmedRecord, PetConfirmedRecordType, byTypeFilterGenerator } from '@app/objects/PetConfirmedRecord';
import { WarningAlert } from '@app/components/UI/Alerts/WarningAlert';
import { iKey } from '@app/components/Pages/PetEditor/Services';
import { hasMemorialItems } from '@app/objects/Pet';
import { getServiceTypePreferences } from '@app/objects/Crematory';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';

function filterRecords(records: Array<PetConfirmedRecord>) {
	const filter = byTypeFilterGenerator(PetConfirmedRecordType.NoSpecialServices);

	return records
		.filter(filter)
		.sort((a, b) => b.time - a.time);
}

export const NoSpecialServicesConfirmation: React.FC = () => {
	const { t } = useTranslation('translation');
	const { values, setFieldValue } = useFormikContext<FormValues>();
	const user = useSelector((state: ApplicationState) => state.login.user);
	const allServices = [...(values.services), ...(values.products), ...(values.urns)];
	const records = filterRecords(values.petConfirmedRecords);
	const crematory = useCrematory();

	if (values.serviceType === undefined) return null;
	if (hasMemorialItems(allServices)) return null;
	if (records.some((record) => record.isMaster)) return null;

	const preferences = getServiceTypePreferences(values.serviceType, crematory);
	if (!preferences?.enableSpecialServiceAcknowledgement) return null;

	const confirmation = activePermission(user, Permission.MasterConfirmation)
		? {
			checkbox: {
				text: t(iKey('warnings.no-memorial-items.master-confirm')),
				checked: values.noSpecialServicesMasterAcknowledged,
				onChange: (checked) => {
					setFieldValue('noSpecialServicesMasterAcknowledged', checked, false);
					setFieldValue('noSpecialServicesAcknowledged', checked, false);
				},
			},
		} : {
			checkbox: {
				text: t(iKey('warnings.no-memorial-items.confirm')),
				checked: values.noSpecialServicesAcknowledged,
				onChange: (checked) => setFieldValue('noSpecialServicesAcknowledged', checked, true),
			},
		};

	return (
		<WarningAlert
			isVisible
			message={t(iKey('warnings.no-memorial-items.message'))}
			description={t(iKey('warnings.no-memorial-items.description'))}
			confirmation={confirmation}
			validationMessage={{
				name: 'noSpecialServicesAcknowledged',
			}}
		/>
	);
};
